import { memo, useCallback, useMemo } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    displayAccountLabel,
    getFallbackAccountRoleForLineItem,
    LineItemFallbackAccountType,
} from "@common/helpers/accounting";
import { findAccountByRole } from "@common/helpers/accrualAccount";
import type { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useLedgers } from "@api/ledger.api";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useScope } from "@state/scopeContext.lib";
import {
    findPrimaryLedgerId,
    readOverrideAccountId,
} from "@lib/accountOverrides";
import { buildLineItemAccountUpdate } from "./buildLineItemAccountUpdate";
import type { AccountOverrideEntry } from "@common/types/transaction";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";

export interface UpdateLineItemAccountParams {
    workspaceId: string;
    lineItemId: string;
    accountOverrides?: AccountOverrideEntryRequest[];
    accountId?: string | null;
}

interface AccrualAccountCellProps {
    lineItemId: string;
    lineItemAccountId?: string | null;
    accountOverrides?: AccountOverrideEntry[];
    ledgerId?: string;
    ledgerName?: string;
    fallbackMode: LineItemFallbackAccountType;
    entityId?: number;
    isLockedBySchedule?: boolean;
    /**
     * Disables the cell for another reason (e.g. the invoice's period is
     * locked). Checked after `isLockedBySchedule` — schedule ownership is
     * the more specific explanation when both apply. Pair with `lockedTooltip`.
     */
    isLocked?: boolean;
    /** Tooltip shown when `isLocked` is true. */
    lockedTooltip?: string;
    onUpdateLineItemAccount: (
        params: UpdateLineItemAccountParams,
    ) => Promise<unknown>;
}

const LINE_ITEM_ALLOWED_ACCOUNT_TYPES: Partial<
    Record<LineItemFallbackAccountType, Array<Account["type"]>>
> = {
    [LineItemFallbackAccountType.billAccountsPayable]: [
        AccountType.accountsPayable,
    ],
    [LineItemFallbackAccountType.invoiceAccountsReceivable]: [
        AccountType.accountsReceivable,
    ],
};

export const AccrualAccountCell = memo(
    ({
        lineItemId,
        lineItemAccountId,
        accountOverrides,
        ledgerId,
        ledgerName,
        fallbackMode,
        entityId,
        isLockedBySchedule = false,
        isLocked = false,
        lockedTooltip,
        onUpdateLineItemAccount,
    }: AccrualAccountCellProps) => {
        const { workspaceId } = useScope();
        const showAccountNumbers = useShowAccountNumbers();
        const { error } = useToaster();

        const { data: ledgers } = useLedgers(entityId);
        const hasAccrualLedger = useMemo(
            () =>
                ledgers?.some(
                    (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
                ) ?? false,
            [ledgers],
        );

        const canEditWithoutAccrualLedger =
            fallbackMode === LineItemFallbackAccountType.invoiceRevenue ||
            fallbackMode === LineItemFallbackAccountType.billExpense;
        const { accounts } = useAccounts({
            entityId:
                hasAccrualLedger || canEditWithoutAccrualLedger
                    ? entityId
                    : undefined,
            ledgerBasis: hasAccrualLedger
                ? AccountingLedgerBasis.accruals
                : AccountingLedgerBasis.cash,
        });

        const fallbackAccountRole = getFallbackAccountRoleForLineItem({
            lineItemFallbackAccountType: fallbackMode,
        });
        const fallbackAccountId = useMemo(
            () => findAccountByRole(accounts, fallbackAccountRole)?.id ?? null,
            [accounts, fallbackAccountRole],
        );
        const accrualLedger = useMemo(
            () =>
                ledgerId
                    ? ledgers?.find((ledger) => ledger.id === ledgerId)
                    : ledgers?.find(
                          (ledger) =>
                              ledger.basis === AccountingLedgerBasis.accruals &&
                              ledger.name === ledgerName,
                      ),
            [ledgerId, ledgerName, ledgers],
        );
        const isExplicitLedgerColumn = !!ledgerId || !!ledgerName;
        const accrualLedgerId = isExplicitLedgerColumn
            ? accrualLedger?.id
            : findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals);
        const overrideAccountId = readOverrideAccountId(
            accountOverrides,
            accrualLedgerId,
        );
        const shouldUseLineItemAccount = !isExplicitLedgerColumn
            ? true
            : accrualLedger?.isPrimary === true;
        const selectedAccountId =
            (shouldUseLineItemAccount
                ? (overrideAccountId ?? lineItemAccountId)
                : overrideAccountId) ?? fallbackAccountId;

        const accountsById = useMemo(
            () =>
                Object.fromEntries(
                    accounts.map((account) => [account.id, account]),
                ),
            [accounts],
        );
        const account = selectedAccountId
            ? accountsById[selectedAccountId]
            : null;
        const accountLabel = account
            ? displayAccountLabel(account, showAccountNumbers)
            : "";
        const allowedAccountTypes =
            LINE_ITEM_ALLOWED_ACCOUNT_TYPES[fallbackMode];

        const handleChange = useCallback(
            async (selectedAccount: Account | null) => {
                if (!workspaceId) {
                    return;
                }
                if (
                    !accrualLedgerId &&
                    (!canEditWithoutAccrualLedger || isExplicitLedgerColumn)
                ) {
                    return;
                }

                try {
                    await onUpdateLineItemAccount({
                        workspaceId,
                        lineItemId,
                        ...buildLineItemAccountUpdate({
                            selectedAccountId: selectedAccount?.id ?? null,
                            accrualLedgerId,
                        }),
                    });
                } catch {
                    error("Couldn't update account");
                }
            },
            [
                accrualLedgerId,
                canEditWithoutAccrualLedger,
                error,
                isExplicitLedgerColumn,
                lineItemId,
                onUpdateLineItemAccount,
                workspaceId,
            ],
        );

        // A scheduled line's accrual account is owned by the schedule: apply
        // rewrote this very override to the holding account, and editing it by
        // hand would desync the invoice from the application's posted
        // recognition entries.
        if (isLockedBySchedule) {
            return (
                <ViewTableCell>
                    <EditableCellContent
                        isEmpty={!account}
                        readonly
                        truncationFullText={accountLabel}
                        tooltip="This line's account is set by its schedule"
                    >
                        {accountLabel}
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        if (isLocked) {
            return (
                <ViewTableCell>
                    <EditableCellContent
                        isEmpty={!account}
                        readonly
                        truncationFullText={accountLabel}
                        tooltip={lockedTooltip}
                    >
                        {accountLabel}
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        if (
            !entityId ||
            (!canEditWithoutAccrualLedger &&
                (!hasAccrualLedger || !accrualLedgerId))
        ) {
            return (
                <ViewTableCell>
                    <EditableCellContent
                        isEmpty={!account}
                        readonly
                        truncationFullText={accountLabel}
                        tooltip="Create accrual ledger to modify account"
                    >
                        {accountLabel}
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <AccountCombobox
                    showAccountCode={showAccountNumbers}
                    selectedAccount={account ?? undefined}
                    onChange={handleChange}
                    entityId={entityId}
                    allowedAccountTypes={allowedAccountTypes}
                    ledgerBasis={
                        hasAccrualLedger
                            ? AccountingLedgerBasis.accruals
                            : AccountingLedgerBasis.cash
                    }
                />
            </ViewTableCell>
        );
    },
);
